const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat`;

const ERROR_MESSAGES: Record<string, string> = {
  rate_limit: "I'm getting a lot of requests right now. Please try again in a moment.",
  credits_exhausted: "Our AI service is temporarily unavailable. Please [book a consultation](/contact) instead.",
  server_error: "I'm having a little trouble right now. Please try again or [book a free consultation](/contact).",
};

export async function streamChat({
  message,
  conversationId,
  onConversationId,
  onDelta,
  onDone,
  onError,
  signal,
}: {
  message: string;
  conversationId: string | null;
  onConversationId: (id: string) => void;
  onDelta: (token: string) => void;
  onDone: () => void;
  onError: (msg: string) => void;
  signal?: AbortSignal;
}) {
  try {
    const resp = await fetch(CHAT_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
      },
      body: JSON.stringify({ message, conversation_id: conversationId }),
      signal,
    });

    if (!resp.ok || !resp.body) {
      if (resp.status === 429) { onError(ERROR_MESSAGES.rate_limit); return; }
      if (resp.status === 402) { onError(ERROR_MESSAGES.credits_exhausted); return; }
      onError(ERROR_MESSAGES.server_error);
      return;
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let newlineIdx: number;
      while ((newlineIdx = buffer.indexOf("\n")) !== -1) {
        let line = buffer.slice(0, newlineIdx);
        buffer = buffer.slice(newlineIdx + 1);
        if (line.endsWith("\r")) line = line.slice(0, -1);
        if (!line.startsWith("data: ")) continue;
        const jsonStr = line.slice(6).trim();
        if (jsonStr === "[DONE]") { onDone(); return; }
        try {
          const parsed = JSON.parse(jsonStr);
          if (parsed.conversation_id) { onConversationId(parsed.conversation_id); continue; }
          if (parsed.token) onDelta(parsed.token);
        } catch { /* partial */ }
      }
    }
    onDone();
  } catch (e: any) {
    if (e.name === "AbortError") return;
    onError(ERROR_MESSAGES.server_error);
  }
}
